import { btnPrimario, chip, money, segmentos, segmento } from '../../components/ui/estilos';
import { duracion, minutosDesde } from '../salon/mesas';
import type { Seleccion } from './useCobro';

export type Filtro = 'cobrar' | 'abierta' | 'todas';

// Una fila por cuenta cobrable: cada sub-mesa (Mesa 3.1) cuenta aparte.
export interface FilaCuenta {
  key: string; sel: Seleccion; label: string; sub: string;
  min: number; total: number; porCobrar: boolean; pagada: boolean;
}

export function filasDe(mesas: any[], llevar: any[]): FilaCuenta[] {
  const deMesas = mesas.flatMap(m => (m.cuentas || []).map((c: any) => ({
    key: `${m.mesa_id}-${c.subcuenta_id}`,
    sel: { tipo: 'mesa', mesaId: m.mesa_id, subcuenta: c.subcuenta_id } as Seleccion,
    label: `Mesa ${c.etiqueta}`,
    sub: [m.mesero, m.dividida ? `${c.num_items} producto${c.num_items === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ') || 'Mesa',
    min: minutosDesde(m.ocupada_desde), total: Number(c.total) || 0,
    porCobrar: !!m.por_cobrar, pagada: !!c.pagada,
  })));
  // Para llevar: ya se va, siempre está "por cobrar".
  const deLlevar = llevar.map(p => ({
    key: p.comanda_id, sel: { tipo: 'llevar', comandaId: p.comanda_id } as Seleccion,
    label: `Para llevar · ${p.cliente_nombre || 'Cliente'}`,
    sub: [p.mesero, p.cobro_tipo === 'inmediato' ? 'Prepago' : 'Paga al recoger'].filter(Boolean).join(' · '),
    min: minutosDesde(p.created_at), total: parseFloat(p.total) || 0, porCobrar: true, pagada: false,
  }));
  return [...deMesas, ...deLlevar];
}

export const estadoFila = (f: { porCobrar: boolean }) => f.porCobrar
  ? { label: 'Por cobrar', bg: 'var(--cobrar-bg)', fg: 'var(--cobrar-fg)' }
  : { label: 'Abierta', bg: 'var(--well)', fg: 'var(--ink-2)' };

const mismaSel = (a: Seleccion | null, b: Seleccion) => !!a && (a.tipo === 'mesa' && b.tipo === 'mesa'
  ? a.mesaId === b.mesaId && a.subcuenta === b.subcuenta
  : a.tipo === 'llevar' && b.tipo === 'llevar' && a.comandaId === b.comandaId);

export default function ListaCuentas({ filas, filtro, onFiltro, actual, onElegir, onNuevoLlevar, pie }: {
  filas: FilaCuenta[]; filtro: Filtro; onFiltro: (f: Filtro) => void;
  actual: Seleccion | null; onElegir: (s: Seleccion) => void; onNuevoLlevar: () => void;
  pie?: React.ReactNode; // fijo abajo, no se mueve con la lista
}) {
  const visibles = filas.filter(f => filtro === 'todas' || (filtro === 'cobrar' ? f.porCobrar : !f.porCobrar));
  const n = (k: Filtro) => filas.filter(f => k === 'todas' || (k === 'cobrar' ? f.porCobrar : !f.porCobrar)).length;

  return (
    <aside aria-label="Cuentas" style={{ borderRight: '1px solid var(--line)', background: 'var(--surface)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '16px 16px 12px', display: 'flex', flexDirection: 'column', gap: 12, flex: 'none' }}>
        <button onClick={onNuevoLlevar} style={{ ...btnPrimario, height: 52, fontSize: 17 }}>Nuevo para llevar</button>
        <div role="tablist" style={segmentos}>
          {([['cobrar', 'Por cobrar'], ['abierta', 'Abiertas'], ['todas', 'Todas']] as const).map(([k, l]) => (
            <button key={k} role="tab" aria-selected={filtro === k} onClick={() => onFiltro(k)}
              aria-label={`${l}: ${n(k)}`}
              style={{ ...segmento(filtro === k), flex: 1, height: 44, fontSize: 15, justifyContent: 'center', padding: '0 6px', whiteSpace: 'nowrap' }}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visibles.map(f => {
          const on = mismaSel(actual, f.sel), st = estadoFila(f);
          return (
            <button key={f.key} onClick={() => !f.pagada && onElegir(f.sel)} disabled={f.pagada} aria-pressed={on}
              style={{
                textAlign: 'left', padding: '14px 16px', borderRadius: 14, cursor: f.pagada ? 'default' : 'pointer',
                border: on ? '2px solid var(--brand-texto)' : '1px solid var(--line)', background: on ? 'var(--well)' : 'var(--surface)',
                display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--ink)', opacity: f.pagada ? .45 : 1,
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{f.label}</span>
                <span style={chip(st.bg, st.fg)}>{f.pagada ? 'Pagada' : st.label}</span>
              </div>
              <span style={{ fontSize: 14, color: 'var(--muted)' }}>{f.sub}</span>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
                <span style={{ color: 'var(--muted)' }}>{duracion(f.min)}</span>
                <span style={{ fontWeight: 700, fontSize: 17 }}>{money(f.total)}</span>
              </div>
            </button>
          );
        })}
        {!visibles.length && <div style={{ padding: '24px 12px', textAlign: 'center', color: 'var(--muted)', fontSize: 15 }}>No hay cuentas en este filtro.</div>}
      </div>
      {pie && <div style={{ flex: 'none', padding: 12, borderTop: '1px solid var(--line)', background: 'var(--surface)' }}>{pie}</div>}
    </aside>
  );
}
